import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listReturns } from '@/api/returns';
import { PageHeader } from '@/components/ui/PageHeader';
import { ExportControls } from '@/components/ui/ExportControls';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { reasonLabel } from '@/constants/defaults';
import { lastNDaysRange } from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';
import { formatCurrency, formatDate } from '@/lib/format';
import { avatarGradient } from '@/lib/returnRow';
import { cn } from '@/lib/cn';
import type { Page, ReturnRecord } from '@/types';
import { paths } from '@/router/paths';

const QC_STATUSES = 'RECEIVED_AT_WAREHOUSE,QC_FAILED_PENDING_REVIEW, PENDING_QC';
const PAGE_SIZE = 20;
const PRODUCT_TITLE_LIMIT = 20;

function truncateTitle(title: string): string {
  return title.length > PRODUCT_TITLE_LIMIT ? `${title.slice(0, PRODUCT_TITLE_LIMIT)}...` : title;
}

export function QcQueueScreen() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [range, setRange] = useState<DateRange>(() => lastNDaysRange(30));

  const { data, errorMessage, isLoading, reload } = useAsyncData<Page<ReturnRecord> | null>(
    (signal) =>
      listReturns({ statuses: QC_STATUSES, page, size: PAGE_SIZE, from: range.from, to: range.to }, signal),
    [page, range],
  );

  const columns: Column<ReturnRecord>[] = [
    {
      key: 'returnId',
      header: 'Request',
      render: (row) => (
        <div className="min-w-0">
          <div className="font-mono text-xs font-semibold text-ink">{row.returnId}</div>
          {row.shopifyOrderNumber ? (
            <div className="truncate font-mono text-xs text-ink-subtle">{row.shopifyOrderNumber}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (row) => (
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-ink">{row.customerName || '—'}</div>
          <div className="truncate text-xs text-ink-muted">{row.customerEmail ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (row) => {
        const firstItem = row.items?.[0];
        const itemCount = row.items?.length ?? 0;
        return (
          <div className="flex min-w-0 items-center gap-3">
            {firstItem?.imageUrl ? (
              <img src={firstItem.imageUrl} alt="" className="h-9 w-9 flex-shrink-0 rounded-lg object-cover" />
            ) : (
              <span
                className={cn('h-9 w-9 flex-shrink-0 rounded-lg bg-gradient-to-br', avatarGradient(row.returnId))}
              />
            )}
            <div className="min-w-0">
              <div className="truncate text-sm text-ink">
                {firstItem?.productTitle ? truncateTitle(firstItem.productTitle) : '—'}
              </div>
              {itemCount > 1 ? (
                <div className="truncate text-xs text-ink-subtle">+{itemCount - 1} more</div>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => <span className="text-sm text-ink-muted">{reasonLabel(row.reasonSlug)}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => <span className="text-sm text-ink-muted">{formatDate(row.createdAt) || '—'}</span>,
    },
    // {
    //   key: 'photos',
    //   header: 'Photos',
    //   align: 'center',
    //   render: (row) =>
    //     row.hasPhotos ? <Badge tone="neutral">Attached</Badge> : <span className="text-ink-subtle">—</span>,
    // },
    {
      key: 'amount',
      header: 'Net refund',
      align: 'right',
      render: (row) => <span className="font-medium">{formatCurrency(row.netRefund)}</span>,
    },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'action',
      header: '',
      align: 'right',
      render: (row) => (
        <Button size="sm" variant="secondary" onClick={() => navigate(paths.returnDetail(row.returnId))}>
          Record QC
        </Button>
      ),
    },
  ];

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="QC queue"
        description="Inspect items received at the warehouse."
        actions={
          <>
            <DateRangePicker
              value={range}
              onChange={(nextRange) => {
                setRange(nextRange);
                setPage(0);
              }}
            />
            <ExportControls type="qc_queue" filenamePrefix="qc-queue" range={range} />
          </>
        }
      />
      {isLoading ? (
        <LoadingState message="Loading QC queue…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : (
        <div className="flex flex-col gap-4">
          <DataTable
            columns={columns}
            rows={data?.content ?? []}
            getRowKey={(row) => row.returnId}
            emptyMessage="Nothing waiting for inspection. 🎉"
          />
          {data && data.totalPages > 0 ? (
            <Pagination
              page={data.page ?? page}
              totalPages={data.totalPages}
              totalElements={data.totalElements}
              onPageChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
